import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import WhatsAppButton from "../../components/feature/WhatsAppButton";

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      body.append("recipient", "info@primeshinewa.com");
      body.append("_replyto", "info@primeshinewa.com");
      await fetch("https://readdy.ai/api/form/d75b5u4prit363gl12jg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSuccess(true);
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      setSuccess(true);
    } finally {
      setSubmitting(false);
    }
  };

  const services = [
    "Commercial Office Cleaning",
    "Medical / Clinic Cleaning",
    "Car Dealership Cleaning",
    "Residential Cleaning",
    "Deep Cleaning",
    "Move In / Move Out",
    "Airbnb / Short-term Rental",
    "Other",
  ];

  const whatsappMsg = encodeURIComponent("Hi Prime Shine WA! I'd like to get a free quote.");

  return (
    <div style={{ fontFamily: "'Poppins', sans-serif" }}>
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 pb-20 bg-[#0F2A44] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20navy%20blue%20and%20gold%20geometric%20design%2C%20minimalist%20premium%20luxury%20texture%20background%2C%20high%20resolution%20corporate%20texture%2C%20elegant%20professional%20visual%20for%20contact%20page&width=1920&height=600&seq=contactbg001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-4 block">
            {t("contact_label")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-5">
            {t("contact_headline")}
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t("contact_sub")}
          </p>
          <div className="flex justify-center mt-8 text-sm text-white/50">
            <Link to="/" className="hover:text-[#D4AF37] transition-colors duration-200">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-[#D4AF37]">Contact</span>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">

            {/* Left Info */}
            <div className="lg:col-span-2 space-y-5">
              <div>
                <h2 className="text-2xl font-bold text-[#0F2A44] mb-2">Get In Touch</h2>
                <p className="text-gray-500 text-sm leading-relaxed">Fill out the form and our team will contact you within a few hours with a personalized quote.</p>
              </div>

              {[
                { icon: "ri-phone-line", title: "Phone", value: "+1 (206) 474-4668", href: "tel:+12064744668" },
                { icon: "ri-mail-line", title: "Email", value: "info@primeshinewa.com", href: "mailto:info@primeshinewa.com" },
                { icon: "ri-map-pin-2-line", title: t("contact_area"), value: t("contact_area_value"), href: null },
                { icon: "ri-time-line", title: "Working Hours", value: t("contact_hours"), href: null },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4 p-5 rounded-xl bg-[#F5F5F5] hover:bg-[#D4AF37]/5 transition-colors duration-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#D4AF37]/15 shrink-0">
                    <i className={`${item.icon} text-lg text-[#D4AF37]`} />
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-0.5">{item.title}</p>
                    {item.href ? (
                      <a href={item.href} className="text-[#0F2A44] font-medium text-sm hover:text-[#D4AF37] transition-colors duration-200">{item.value}</a>
                    ) : (
                      <p className="text-[#0F2A44] font-medium text-sm">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}

              <a
                href={`sms:+12064744668?body=${whatsappMsg}`}
                className="flex items-center gap-3 bg-[#0F2A44] text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 hover:bg-[#1a4a7a] hover:scale-105 cursor-pointer"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-message-2-fill text-xl text-[#D4AF37]" />
                </div>
                <span className="text-sm whitespace-nowrap">Send Us a Text Message</span>
              </a>

              {/* Service Area Map Placeholder */}
              <div className="rounded-xl overflow-hidden h-40">
                <img
                  src="https://readdy.ai/api/search-image?query=satellite%20map%20view%20of%20Seattle%20Washington%20state%20downtown%2C%20green%20and%20blue%20tones%2C%20clean%20aerial%20photography%2C%20urban%20commercial%20district%2C%20professional%20map%20style%20view&width=500&height=200&seq=map001&orientation=landscape"
                  alt="Seattle service area"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-3 rounded-2xl p-8" style={{ boxShadow: "0 4px 30px rgba(0,0,0,0.07)" }}>
              {success ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                    <i className="ri-check-double-line text-3xl text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F2A44] mb-2">Message Sent!</h3>
                  <p className="text-gray-500 text-sm">{t("contact_success")}</p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="mt-6 text-[#D4AF37] text-sm font-semibold cursor-pointer hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                  <input type="hidden" name="to" value="info@primeshinewa.com" />
                  <div>
                    <h3 className="text-xl font-bold text-[#0F2A44] mb-1">Request Free Quote</h3>
                    <p className="text-gray-400 text-sm mb-6">We'll respond within a few hours.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-[#0F2A44] uppercase tracking-wide mb-2">{t("contact_name")} *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-200"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#0F2A44] uppercase tracking-wide mb-2">{t("contact_email")} *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-200"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-[#0F2A44] uppercase tracking-wide mb-2">{t("contact_phone")}</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-200"
                        placeholder="+1 (206) 555-0000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-[#0F2A44] uppercase tracking-wide mb-2">{t("contact_service")}</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-200 bg-white"
                      >
                        <option value="">Select a service...</option>
                        {services.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#0F2A44] uppercase tracking-wide mb-2">{t("contact_message")}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition-all duration-200 resize-none"
                      placeholder="Tell us about your cleaning needs — facility type, size, frequency..."
                    />
                    <p className="text-right text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || formData.message.length > 500}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8952E] disabled:opacity-60 text-[#0F2A44] font-bold py-4 rounded-xl text-base transition-all duration-200 hover:scale-[1.02] whitespace-nowrap cursor-pointer"
                    style={{ boxShadow: "0 4px 16px rgba(212,175,55,0.3)" }}
                  >
                    {submitting ? t("contact_submitting") : t("contact_submit")}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ContactPage;
